/// Texto formatado do Prontuário: negrito, itálico, sublinhado, parágrafo, quebra e listas, e nada
/// mais (ADR 0038, decisão 3).
///
/// É o mesmo princípio dos modelos de e-mail — lista fechada, recusa por padrão —, mas aqui o texto
/// é **saneado** em vez de recusado: quem escreve é a profissional no balcão, colando de onde vier,
/// e perder a resposta por causa de um `<span style>` que o navegador inventou seria punir a
/// pessoa pelo editor. Marca desconhecida some e o texto dela fica; conteúdo de script e estilo
/// some inteiro.
///
/// Mora no contrato porque o servidor saneia antes de gravar e a tela saneia de novo antes de
/// desenhar: a segunda passada é defesa, e as duas precisam concordar na mesma lista.

const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'ul', 'ol', 'li']);
const droppedWithContent = new Set([
  'script',
  'style',
  'template',
  'iframe',
  'object',
  'noscript',
  'textarea',
  'title',
]);
const tagPattern = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*>|<!--[\s\S]*?(?:-->|$)|<[!?][^>]*>?/gu;

export function sanitizeRichText(html: string): string {
  const out: string[] = [];
  const open: string[] = [];
  let skipping: string | null = null;
  let cursor = 0;
  for (const match of html.matchAll(tagPattern)) {
    if (!skipping) out.push(escapeText(html.slice(cursor, match.index)));
    cursor = match.index + match[0].length;
    const name = match[2]?.toLowerCase();
    if (!name) continue;
    const closing = match[1] === '/';
    if (skipping) {
      if (closing && name === skipping) skipping = null;
      continue;
    }
    if (droppedWithContent.has(name)) {
      if (!closing) skipping = name;
      continue;
    }
    // O editor do navegador quebra linha com `<div>`: vira parágrafo, que é o que a pessoa quis.
    const tag = name === 'div' ? 'p' : name;
    if (!allowedTags.has(tag)) continue;
    if (tag === 'br') out.push('<br>');
    else if (!closing) {
      out.push(`<${tag}>`);
      open.push(tag);
    } else {
      const at = open.lastIndexOf(tag);
      while (at !== -1 && open.length > at) out.push(`</${open.pop()!}>`);
    }
  }
  if (!skipping) out.push(escapeText(html.slice(cursor)));
  while (open.length) out.push(`</${open.pop()!}>`);
  return out.join('');
}

/// O texto sem marcação, com os blocos separados por espaço: é o que a comparação entre versões e
/// o Quadro de saúde leem, e o que decide se a resposta está vazia.
export function richTextToPlain(html: string): string {
  return decodeEntities(
    sanitizeRichText(html)
      .replace(/<br>|<\/(?:p|li)>/gu, ' ')
      .replace(/<[^>]+>/gu, ''),
  )
    .replace(/\s+/gu, ' ')
    .trim();
}

function escapeText(text: string): string {
  return text
    .replace(/&(?!(?:[a-z]+|#\d+|#x[0-9a-f]+);)/giu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;');
}

const namedEntities: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/giu, (entity, body: string) => {
    if (body.startsWith('#')) {
      const code =
        body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : Number(body.slice(1));
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    }
    return namedEntities[body.toLowerCase()] ?? entity;
  });
}
